import { API, graphqlOperation } from 'aws-amplify'
import { useEffect, useState, useRef, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { addTenants, setTenants } from '../../../redux/admin/actions'

// Deep-merge acknowledgedBy fields (d2, pk) from existing data into incoming data.
// This handles the case where one query returned richer acknowledgement details
// and a later query from a different tab overwrites Redux with sparser data.
const deepMergeAcknowledgedBy = (incomingAcks, existingAcks) => {
    if (!existingAcks?.acknowledgements || !incomingAcks?.acknowledgements) return incomingAcks;

    const existingByEmail = new Map();
    for (const ack of existingAcks.acknowledgements) {
        const email = ack?.acknowledgedBy?.email;
        if (email) existingByEmail.set(email.toLowerCase(), ack.acknowledgedBy);
    }
    if (existingByEmail.size === 0) return incomingAcks;

    const mergedInnerAcks = incomingAcks.acknowledgements.map(ack => {
        if (!ack?.acknowledgedBy) return ack;
        const { d2, pk, email } = ack.acknowledgedBy;
        if (d2 && pk) return ack;

        const existing = email ? existingByEmail.get(email.toLowerCase()) : null;
        if (!existing) return ack;

        return {
            ...ack,
            acknowledgedBy: {
                ...ack.acknowledgedBy,
                d2: d2 || existing.d2,
                pk: pk || existing.pk,
                workPhone: ack.acknowledgedBy.workPhone || existing.workPhone,
                phone: ack.acknowledgedBy.phone || existing.phone,
            },
        };
    });

    return { ...incomingAcks, acknowledgements: mergedInnerAcks };
};

// Merge acknowledgement details from an existing tenants list into a newly fetched list.
// This prevents slimmer queries (that omit acknowledgements) from wiping that data
// in shared Redux state when different tabs fetch tenants with different field selections.
const mergeAcknowledgements = (incoming = [], existing = [], incomingOmitsAcks = false) => {
    if (!existing || existing.length === 0) {
        return incoming;
    }

    const existingByPk = new Map(
        existing
            .filter(item => item?.pk)
            .map(item => [item.pk, item])
    );

    if (existingByPk.size === 0) return incoming;

    return incoming.map(item => {
        if (!item) return item;
        const existingItem = existingByPk.get(item.pk);
        if (!existingItem) return item;

        if (incomingOmitsAcks) {
            // Query didn't request acknowledgements — carry them over from Redux
            return existingItem.acknowledgements
                ? { ...item, acknowledgements: existingItem.acknowledgements }
                : item;
        }

        // Query DID include acknowledgements — deep-merge d2/pk into acknowledgedBy
        if (item.acknowledgements && existingItem.acknowledgements) {
            return {
                ...item,
                acknowledgements: deepMergeAcknowledgedBy(item.acknowledgements, existingItem.acknowledgements),
            };
        }

        return item;
    });
};

const query = `
    query ListTenants($sk: String!, $d1: String, $limit: Int, $nextToken: String) {
        listTenants(sk: $sk, d1: $d1, limit: $limit, nextToken: $nextToken) {
            items {
                pk
                sk
                d1
                floors
                name
                suites
                building
                property
                metaData {
                    createdAt
                }
                acknowledgements {
                    pk
                    sk
                    d1
                    acknowledgements {
                        active
                        declined
                        timestamp
                        acknowledgedBy {
                            pk
                            d2
                            firstName
                            lastName
                            email
                            workPhone {
                                number
                                extension
                            }
                            phone {
                                number
                                extension
                            }
                        }
                    }
                    building
                    tenant
                }
            }
            nextToken
        }
    }
`

const queryWithoutAcknowledgements = `
    query ListTenants($sk: String!, $d1: String, $limit: Int, $nextToken: String) {
        listTenants(sk: $sk, d1: $d1, limit: $limit, nextToken: $nextToken) {
            items {
                pk
                sk
                d1
                floors
                name
                suites
                building
                property
                metaData {
                    createdAt
                }
            }
            nextToken
        }
    }
`

const queryWithAdmin = `
    query ListTenants($sk: String!, $d1: String, $limit: Int, $nextToken: String) {
        listTenants(sk: $sk, d1: $d1, limit: $limit, nextToken: $nextToken) {
            items {
                pk
                sk
                d1
                floors
                name
                suites
                building
                property
                metaData {
                    createdAt
                }
                acknowledgements {
                    pk
                    sk
                    d1
                    building
                    tenant
                    acknowledgements {
                        active
                        declined
                        timestamp
                        acknowledgedBy {
                            pk
                            d2
                            firstName
                            lastName
                            email
                            workPhone {
                                number
                                extension
                            }
                            phone {
                                number
                                extension
                            }
                        }
                    }
                }
                admins {
                    pk
                    sk
                    d1
                    d2
                    firstName
                    lastName
                    email
                }
            }
            nextToken
        }
    }
`

// Enhanced cache with timestamp for expiration
const cache = {}
// Cache TTL in milliseconds (5 minutes)
const CACHE_TTL = 5 * 60 * 1000;
// Minimum time between requests in milliseconds (1 second)
const THROTTLE_TIME = 1000;

// Module-level flag: when true the next fetchData (even on fresh mount) will
// skip deepMergeAcknowledgedBy so Lambda changes to d2/pk are not overwritten.
let pendingSkipMerge = false;

// Call this from outside React (e.g. API.js) to wipe the cache and mark the
// next fetch as a hard refresh. Safe to call even when Tenants page is unmounted.
export const clearTenantsCache = () => {
    Object.keys(cache).forEach(key => delete cache[key]);
    pendingSkipMerge = true;
};

function useListTenants(d1, withAdmins, noAcknowledgements, limit = 500) {
    const [errors, setErrors] = useState(null)
    const [loading, setLoading] = useState(true)
    const [nextToken, setNextToken] = useState(null)
    const tenants = useSelector((state) => state.admin.tenants)
    const isLoadingRef = useRef(false)
    const lastFetchTimeRef = useRef(0)

    // Keep a ref to always read the latest Redux tenants inside async callbacks,
    // avoiding stale closures without adding tenants to the useCallback dep array.
    const tenantsRef = useRef(tenants);
    useEffect(() => { tenantsRef.current = tenants; }, [tenants]);

    // When set, the next fetchData call will use raw AppSync data without merging
    // stale d2/pk from Redux. Used after archive/restore events so that the Lambda's
    // changes (stripping or re-adding d2/pk) are not overwritten by the cached state.
    const skipMergeRef = useRef(false);

    const fetchData = useCallback(async () => {
        const cacheKey = `${d1 || 'SUPER'}_${withAdmins ? 'with-admins' : ''}_${noAcknowledgements ? 'no-ack' : ''}`;
        const currentTime = Date.now();

        if (isLoadingRef.current) {
            return;
        }

        if (currentTime - lastFetchTimeRef.current < THROTTLE_TIME) {
            return;
        }

        if (!nextToken &&
            cache[cacheKey] &&
            cache[cacheKey].timestamp &&
            currentTime - cache[cacheKey].timestamp < CACHE_TTL) {

            const cachedItems = cache[cacheKey].data || [];
            const mergedCachedItems = mergeAcknowledgements(cachedItems, tenantsRef.current, noAcknowledgements);
            if (d1 !== 'SUPER') {
                setTenants(mergedCachedItems);
            } else {
                addTenants(mergedCachedItems);
            }
            setLoading(false);
            return;
        }

        try {
            isLoadingRef.current = true;
            lastFetchTimeRef.current = currentTime;

            let _query
            if (withAdmins) {
                _query = queryWithAdmin
            } else if (noAcknowledgements) {
                _query = queryWithoutAcknowledgements
            } else {
                _query = query
            }

            const response = await API.graphql(
                graphqlOperation(_query, {
                    d1: d1 === 'SUPER' ? null : d1,
                    limit: limit,
                    nextToken: nextToken,
                    sk: 'TENANT',
                })
            );

            const shouldSkipMerge = skipMergeRef.current || pendingSkipMerge;
            skipMergeRef.current = false;
            pendingSkipMerge = false;

            const mergedItems = shouldSkipMerge
                ? (response.data.listTenants.items || [])
                : mergeAcknowledgements(
                    response.data.listTenants.items || [],
                    tenantsRef.current,
                    noAcknowledgements
                );

            cache[cacheKey] = {
                data: mergedItems,
                timestamp: Date.now()
            };

            if (response.data.listTenants.nextToken) {
                setNextToken(response.data.listTenants.nextToken)
            }

            if (d1 !== 'SUPER') {
                setTenants(mergedItems)
            } else {
                addTenants(mergedItems)
            }

            setLoading(false)
        } catch (err) {
            console.error('Error fetching tenants:', err);
            setErrors(err)
        } finally {
            isLoadingRef.current = false;
        }
    }, [d1, withAdmins, noAcknowledgements, limit, nextToken]);

    // Memoize invalidateCache to prevent stale closures
    const invalidateCache = useCallback(() => {
        const cacheKey = `${d1 || 'SUPER'}_${withAdmins ? 'with-admins' : ''}_${noAcknowledgements ? 'no-ack' : ''}`;
        if (cache[cacheKey]) {
            delete cache[cacheKey];
        }
    }, [d1, withAdmins, noAcknowledgements]);

    // Stable ref to the latest fetchData. The d1 effect uses this instead of depending
    // directly on fetchData, which changes whenever nextToken changes and would otherwise
    // re-trigger the d1 effect during pagination (the pagination effect handles that separately).
    const fetchDataRef = useRef(fetchData);
    useEffect(() => { fetchDataRef.current = fetchData; });

    // Add event listener to clear cache when tenant data changes
    useEffect(() => {
        // Function to handle tenant data updated event
        const handleTenantDataUpdated = () => {
            invalidateCache();
            // Skip the deepMergeAcknowledgedBy step so the fresh AppSync data
            // (which may have d2/pk stripped or restored by a Lambda) is used as-is
            // rather than being overwritten by the stale Redux state.
            skipMergeRef.current = true;
            setLoading(true);
            fetchData();
        };

        // Add event listener
        window.addEventListener('tenant-data-updated', handleTenantDataUpdated);

        // Cleanup listener on unmount
        return () => {
            window.removeEventListener('tenant-data-updated', handleTenantDataUpdated);
        };
    }, [invalidateCache, fetchData]);

    // Handle pagination
    useEffect(() => {
        if (nextToken) {
            fetchData()
        }
    }, [nextToken, fetchData])

    // Initial data fetch when d1 changes
    useEffect(() => {
        if (!d1) {
            setLoading(false)
            return
        }

        // Mark as loading immediately so stale cross-scope Redux data (e.g. from a
        // previous SUPER-level query) isn't rendered while the scoped fetch is in flight.
        setLoading(true);

        // Reset nextToken when d1 changes to ensure a fresh query
        setNextToken(null);

        // Delay the first fetch slightly to allow for batching of multiple hook calls
        const timer = setTimeout(() => {
            fetchDataRef.current()
        }, 100);

        return () => clearTimeout(timer);
    // fetchData intentionally excluded — use fetchDataRef to avoid re-triggering
    // this effect on nextToken changes (pagination is handled by its own effect).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [d1, withAdmins, noAcknowledgements])

    return { errors, tenants, loading, fetchData, invalidateCache }
}

export default useListTenants
